import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLanguageCodeFromURL, isLanguageCodeValid, setLanguageCodeInURL,  } from './utils/utils.ts';
import { LanguageCode, fallbackLanguage } from './language.ts';

// Importing translation resources
import { navigationResource, NavigationResourceTranslations } from '../components/navigation/Navigation.i18n.ts';
import { aboutMeResource, AboutMeResourceTranslations } from '../components/aboutMe/AboutMe.i18n.ts';
import { projectsResource, ProjectsResourceTranslations } from '../components/projects/Projects.i18n.ts';
import { projectUrlsResource, ProjectUrlsResourceTranslations } from '../components/projects/components/project/components/projectUrls/ProjectUrls.i18n.ts';
import { footerResource, FooterResourceTranslations } from '../components/footer/Footer.i18n.ts';

const initialLanguageCode: LanguageCode = (() => {
    const languageCodeFromURL = getLanguageCodeFromURL();

    // If the language code from the URL is valid, use it
    if (
        languageCodeFromURL && 
        isLanguageCodeValid(languageCodeFromURL)
    ) {
        return languageCodeFromURL as LanguageCode;
    }
    
    // If not, set the URL language query parameter to the fallback language and use the fallback language code
    setLanguageCodeInURL(fallbackLanguage);
    return fallbackLanguage;
})();


// Updates the language URL query parameter whenever the language changes in i18n.
// We make use of the `onLanguageChanged` event provided by i18next.
// DOCS: https://www.i18next.com/overview/api#onlanguagechanged
i18n.on('languageChanged', (languageCode: LanguageCode) => {
    setLanguageCodeInURL(languageCode);
});


/**
 * Overall translation resources structure.
 */
type Resources = {
    [Language in LanguageCode]: {
        navigation: NavigationResourceTranslations;
        aboutMe: AboutMeResourceTranslations;
        projects: ProjectsResourceTranslations;
        projectUrls: ProjectUrlsResourceTranslations;
        footer: FooterResourceTranslations;
    };
};

export const resources: Resources = {
    en: {
        navigation: navigationResource.en,
        aboutMe: aboutMeResource.en,
        projects: projectsResource.en,
        projectUrls: projectUrlsResource.en,
        footer: footerResource.en
    },
    nl: {
        navigation: navigationResource.nl,
        aboutMe: aboutMeResource.nl,
        projects: projectsResource.nl,
        projectUrls: projectUrlsResource.nl,
        footer: footerResource.nl
    }
} as const;

/**
 * i18next configuration
 * - `resources`: Translation resources.
 * - `lng`: Default language.
 * - `fallbackLng`: Fallback language if translation is missing.
 * - `interpolation.escapeValue`: Renders values as is (not escaped).
 */
i18n.use(initReactI18next).init({
    resources,
    lng: initialLanguageCode,
    fallbackLng: fallbackLanguage,
    interpolation: { 
        escapeValue: false 
    }
});

export default i18n;